/**
 * ScorePanel
 *
 * Right-rail panel for the BattleScreen, modelled on NSCL's panel
 * (nscl/src/app/components/battle/BattleScreen.tsx). Vertical stack:
 *
 *   ┌─ Opponent: avatar + name on the left, teal score badge on the right ─┐
 *   ├─ MoveRow:  most recent move dots (green = correct, red = wrong)      │
 *   ├─ Timer:    SVG circular ring with the seconds in the middle          │
 *   ├─ MoveRow:  my own move history                                       │
 *   ├─ You:      avatar + name + "You" pill, teal score badge              │
 *   └─ End Game button (red outline)                                       ─┘
 *
 * All visuals are pure controlled — the BattleScreen owns timer math and
 * forfeit dispatch.
 */

import React, {useEffect, useRef, useState} from 'react';
import {Animated, Image, Pressable, StyleSheet, View} from 'react-native';
import Svg, {Circle} from 'react-native-svg';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';

const GOLDEN = '#D1AB41';
const TEAL = '#3DAB9E';
const WRONG = '#EB5757';
const WARNING = '#E17B21';
const BORDER = '#D9D9D9';
const TEXT_DARK = '#262322';
const BG_PAGE = '#FAF6EB';

const RING_SIZE = 200;
const RING_RADIUS = 88;
const RING_STROKE = 8;
const CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

interface ScorePanelProps {
  myCorrect: number;
  opponentCorrect: number;
  /**
   * Wrong-counts for both players. Drives the optional "× N of M" subtitle
   * under each score badge and the warning pulse at maxWrong - 1.
   */
  myWrong?: number;
  opponentWrong?: number;
  /** Boolean trail of my recent moves: `true` = correct, `false` = wrong. */
  myMoves: boolean[];
  /** Boolean trail of opponent's recent moves. */
  opponentMoves: boolean[];
  totalSec: number;
  /** Seconds remaining as computed by the parent (kept in sync with server endsAt). */
  secondsLeft: number;
  myName: string;
  opponentName: string;
  myAvatarUrl?: string;
  opponentAvatarUrl?: string;
  /**
   * PTB: mistake-out limit. When set, the player's wrong counter shows as
   * "× N of M" and pulses red once when N === M - 1 (one mistake away).
   */
  maxWrong?: number;
  /**
   * PDB: target-correct race target. When set, each player's score badge is
   * paired with a "✓ N of M" subtitle and the centre timer ring is replaced
   * by a "Race to {targetCorrect}" tile (PDB has no primary game timer —
   * `secondsLeft` ships the 10-minute safety cap which we don't surface).
   */
  targetCorrect?: number;
  /** Disable + greys out the End Game button. */
  endGameDisabled?: boolean;
  /** End Game button label override — e.g. "Forfeiting…" while in flight. */
  endGameLabel?: string;
  onPressEndGame?: () => void;
}

export default function ScorePanel(props: ScorePanelProps) {
  const {
    myCorrect,
    opponentCorrect,
    myWrong,
    opponentWrong,
    myMoves,
    opponentMoves,
    totalSec,
    secondsLeft,
    myName,
    opponentName,
    myAvatarUrl,
    opponentAvatarUrl,
    maxWrong,
    targetCorrect,
    endGameDisabled = false,
    endGameLabel,
    onPressEndGame,
  } = props;

  const isRace = typeof targetCorrect === 'number' && targetCorrect > 0;

  const ringColor =
    secondsLeft <= 5 ? WRONG : secondsLeft <= 10 ? WARNING : GOLDEN;
  const ringProgress = totalSec > 0 ? secondsLeft / totalSec : 0;
  const strokeDashoffset = CIRCUMFERENCE * (1 - ringProgress);

  return (
    <Box style={styles.panel}>
      {/* Opponent — top */}
      <PlayerSection
        label={opponentName}
        score={opponentCorrect}
        wrong={opponentWrong}
        maxWrong={maxWrong}
        targetCorrect={targetCorrect}
        isTop
        avatarUrl={opponentAvatarUrl}
      />
      <MoveRow moves={opponentMoves} />

      {/* Centre tile — race header for PDB, timer ring for ST/PTB. PDB has
          no primary timer the user cares about (the 10-min safety cap
          isn't surfaced), so the SVG ring is replaced by a textual
          "Race to N" tile. A future iteration may layer a progress dial. */}
      {isRace ? (
        <View style={styles.raceTile}>
          <CCText style={styles.raceHeader}>RACE TO</CCText>
          <CCText style={styles.raceNumber}>{targetCorrect}</CCText>
          <CCText style={styles.raceSubtitle}>
            First to solve {targetCorrect} wins
          </CCText>
        </View>
      ) : (
        <View style={styles.ringContainer}>
          <View style={styles.ringWrap}>
            <Svg
              width={RING_SIZE}
              height={RING_SIZE}
              style={StyleSheet.absoluteFill as any}>
              <Circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                stroke="#E8E8E8"
                strokeWidth={RING_STROKE}
                fill="none"
              />
              <Circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                stroke={ringColor}
                strokeWidth={RING_STROKE}
                fill="none"
                strokeDasharray={`${CIRCUMFERENCE}`}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
              />
            </Svg>
            <CCText style={[styles.timerNum, {color: TEXT_DARK}]}>
              {secondsLeft}
            </CCText>
          </View>
        </View>
      )}

      {/* You — bottom */}
      <MoveRow moves={myMoves} />
      <PlayerSection
        label={myName}
        score={myCorrect}
        wrong={myWrong}
        maxWrong={maxWrong}
        targetCorrect={targetCorrect}
        isTop={false}
        isYou
        avatarUrl={myAvatarUrl}
      />

      {/* End game */}
      {onPressEndGame && (
        <Pressable
          onPress={onPressEndGame}
          disabled={endGameDisabled}
          style={[
            styles.endGameBtn,
            endGameDisabled && styles.endGameBtnDisabled,
          ]}>
          <CCText style={styles.endGameBtnText}>
            {endGameLabel ?? 'End Game'}
          </CCText>
        </Pressable>
      )}
    </Box>
  );
}

// ── MoveRow — traffic-light history of recent moves ──────────────────────────

function MoveRow({moves}: {moves: boolean[]}) {
  const [rowWidth, setRowWidth] = useState(0);
  const cellSize = 18;
  const gap = 6;
  const horizontalPadding = 32;
  const ellipsisReserve = 24;
  const contentWidth = Math.max(0, rowWidth - horizontalPadding);
  const baseSlots =
    contentWidth > 0
      ? Math.max(1, Math.floor((contentWidth + gap) / (cellSize + gap)))
      : 10;
  const hasOverflow = moves.length > baseSlots;
  const adjustedWidth = hasOverflow
    ? Math.max(0, contentWidth - ellipsisReserve)
    : contentWidth;
  const moveSlots =
    adjustedWidth > 0
      ? Math.max(1, Math.floor((adjustedWidth + gap) / (cellSize + gap)))
      : 1;
  const visibleMoves = moves.slice(-moveSlots);

  // One-shot 1 → 1.2 → 1 scale pulse on the newest chip. Used as the move
  // feedback signal in PTB/PDB now that the on-board tint has been removed.
  // We watch `moves.length` (not the boolean values) so a rejoin that
  // hydrates a long history at once doesn't trigger a pulse — only an
  // incremental append (the live in-battle case) animates.
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const prevLenRef = useRef<number>(moves.length);
  useEffect(() => {
    const prev = prevLenRef.current;
    prevLenRef.current = moves.length;
    if (moves.length <= prev) return;        // hydration / shrink → silent
    if (moves.length - prev !== 1) return;   // only single-step appends pulse
    pulseAnim.setValue(1);
    Animated.sequence([
      Animated.timing(pulseAnim, {
        toValue: 1.2,
        duration: 140,
        useNativeDriver: true,
      }),
      Animated.timing(pulseAnim, {
        toValue: 1,
        duration: 180,
        useNativeDriver: true,
      }),
    ]).start();
  }, [moves.length, pulseAnim]);

  const lastIdx = visibleMoves.length - 1;

  return (
    <View
      style={moveRowStyles.row}
      onLayout={e => setRowWidth(e.nativeEvent.layout.width)}>
      {hasOverflow && <CCText style={moveRowStyles.ellipsis}>...</CCText>}
      {visibleMoves.map((m, idx) => {
        const isLatest = idx === lastIdx;
        const ChipView = isLatest ? Animated.View : View;
        return (
          <ChipView
            key={idx}
            style={[
              moveRowStyles.square,
              m ? moveRowStyles.squareCorrect : moveRowStyles.squareWrong,
              isLatest && {transform: [{scale: pulseAnim}]},
            ]}>
            <CCText style={moveRowStyles.glyph}>{m ? '✓' : '✕'}</CCText>
          </ChipView>
        );
      })}
    </View>
  );
}

// ── PlayerSection — avatar + name on the left, score badge on the right ─────

function PlayerSection({
  label,
  score,
  wrong,
  maxWrong,
  targetCorrect,
  isTop,
  isYou = false,
  avatarUrl,
}: {
  label: string;
  score: number;
  wrong?: number;
  maxWrong?: number;
  targetCorrect?: number;
  isTop: boolean;
  isYou?: boolean;
  avatarUrl?: string;
}) {
  const [imgErr, setImgErr] = useState(false);
  useEffect(() => {
    setImgErr(false);
  }, [avatarUrl]);
  const showPhoto = Boolean(avatarUrl?.trim()) && !imgErr;
  const initial = (label?.[0] ?? '').toUpperCase();

  // Hide the "✗ N of M" chip + warning pulse when the user picked the
  // "Unlimited" affordance (sentinel ≥ 100). "✗ 0 of 100" is meaningless
  // chrome at that point.
  const showWrong =
    typeof wrong === 'number' &&
    typeof maxWrong === 'number' &&
    maxWrong > 0 &&
    maxWrong < 100;
  const wrongCount = wrong ?? 0;

  // One-shot red pulse when N === maxWrong - 1 (one mistake from elimination).
  const pulseAnim = useRef(new Animated.Value(0)).current;
  const prevWrongRef = useRef<number>(wrongCount);
  useEffect(() => {
    if (!showWrong) return;
    const prev = prevWrongRef.current;
    prevWrongRef.current = wrongCount;
    // Only fire when we just crossed into the warning zone (jumped UP to
    // maxWrong-1 from below). A backwards jump (rematch / rejoin) shouldn't
    // re-pulse.
    if (
      wrongCount === (maxWrong as number) - 1 &&
      prev < (maxWrong as number) - 1
    ) {
      pulseAnim.setValue(0);
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 220,
          useNativeDriver: false,
        }),
        Animated.timing(pulseAnim, {
          toValue: 0,
          duration: 320,
          useNativeDriver: false,
        }),
      ]).start();
    }
  }, [wrongCount, maxWrong, showWrong, pulseAnim]);

  const pulseBg = pulseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['rgba(235, 87, 87, 0)', 'rgba(235, 87, 87, 0.85)'],
  });

  return (
    <View
      style={[
        playerStyles.section,
        isTop ? playerStyles.sectionTop : playerStyles.sectionBottom,
      ]}>
      <View style={playerStyles.nameRow}>
        <View style={playerStyles.avatar}>
          {showPhoto ? (
            <Image
              source={{uri: avatarUrl!.trim()}}
              style={playerStyles.avatarImage}
              onError={() => setImgErr(true)}
            />
          ) : (
            <CCText style={playerStyles.avatarText}>{initial}</CCText>
          )}
        </View>
        <View style={playerStyles.nameWithTag}>
          <CCText style={playerStyles.name} numberOfLines={1}>
            {label}
          </CCText>
          {isYou && (
            <View style={playerStyles.youTag}>
              <CCText style={playerStyles.youTagText}>You</CCText>
            </View>
          )}
        </View>
      </View>
      <View style={playerStyles.scoreCol}>
        <View style={playerStyles.scoreBadge}>
          <CCText style={playerStyles.scoreBadgeText}>{score}</CCText>
        </View>
        {typeof targetCorrect === 'number' && targetCorrect > 0 && (
          <View style={playerStyles.targetChip}>
            <CCText style={playerStyles.targetChipText}>
              ✓ {score} of {targetCorrect}
            </CCText>
          </View>
        )}
        {showWrong && (
          <Animated.View
            style={[
              playerStyles.wrongChip,
              {backgroundColor: pulseBg},
            ]}>
            <CCText style={playerStyles.wrongChipText}>
              ✗ {wrongCount} of {maxWrong}
            </CCText>
          </Animated.View>
        )}
      </View>
    </View>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 8,
    overflow: 'hidden',
  },
  ringContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    backgroundColor: '#FFFFFF',
    flex: 1,
  },
  ringWrap: {
    width: RING_SIZE,
    height: RING_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  timerNum: {
    fontSize: 48,
    fontWeight: '500',
  },
  raceTile: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
    paddingHorizontal: 16,
    backgroundColor: '#FFFFFF',
    flex: 1,
  },
  raceHeader: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1.4,
    color: '#4D3F37',
    marginBottom: 4,
  },
  raceNumber: {
    fontSize: 64,
    fontWeight: '800',
    color: TEAL,
    lineHeight: 70,
  },
  raceSubtitle: {
    fontSize: 12,
    color: '#4D3F37',
    marginTop: 6,
    textAlign: 'center',
  },
  endGameBtn: {
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: WRONG,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  endGameBtnDisabled: {
    opacity: 0.5,
  },
  endGameBtnText: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '600',
    color: WRONG,
  },
});

const playerStyles = StyleSheet.create({
  section: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
  },
  sectionTop: {
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },
  sectionBottom: {
    borderTopWidth: 1,
    borderTopColor: BORDER,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  nameWithTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: GOLDEN,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  avatarText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  name: {
    fontSize: 14,
    fontWeight: '500',
    color: TEXT_DARK,
    flexShrink: 1,
  },
  youTag: {
    backgroundColor: BG_PAGE,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  youTagText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#4D3F37',
  },
  scoreCol: {
    alignItems: 'flex-end',
    gap: 4,
  },
  scoreBadge: {
    width: 56,
    height: 56,
    borderRadius: 4,
    backgroundColor: TEAL,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreBadgeText: {
    fontSize: 32,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  wrongChip: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: WRONG,
  },
  wrongChipText: {
    fontSize: 11,
    fontWeight: '700',
    color: WRONG,
  },
  targetChip: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: TEAL,
    backgroundColor: 'rgba(61, 171, 158, 0.10)',
  },
  targetChipText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#1F6C63',
  },
});

const moveRowStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 10,
    overflow: 'hidden',
  },
  ellipsis: {
    color: '#888280',
    fontSize: 14,
    fontWeight: '700',
    marginRight: 2,
  },
  square: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  squareCorrect: {
    backgroundColor: TEAL,
    borderColor: '#2E8A7E',
  },
  squareWrong: {
    backgroundColor: WRONG,
    borderColor: '#C63B3B',
  },
  // White ✓ / ✕ glyph centered inside each history chip. Font weight + size
  // tuned so the mark reads cleanly at the 18px chip size — matches the
  // tick/cross icons the user referenced.
  glyph: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '900',
    lineHeight: 14,
    textAlign: 'center',
  },
});
